import { MemberPreference } from 'node-api/members/member.types';
import { Reducer } from 'react';
import { Action as DefaultAction } from 'state/types';

export enum ActionTypes {
  CLEAR = 'CLEAR',
  CLEAR_MESSAGE = 'CLEAR_MESSAGE',
  SELECT = 'SELECT',
  SET = 'SET',
  SET_ASSIGNED_PROVIDERS = 'SET_ASSIGNED_PROVIDERS',
  SET_MESSAGE = 'SET_MESSAGE',
  SET_PREFERENCES = 'SET_PREFERENCES',
  REVIEW = 'REVIEW',
  REVIEW_MANY = 'REVIEW_MANY',
  SET_PROGRAM = 'SET_PROGRAM',
  SET_CLINIC_FILTER = 'SET_CLINIC_FILTER',
}

export interface Member {
  assignedProviders?: Provider[];
  app_version?: string;
  avatar?: {
    thumb?: {
      url: string;
    };
  };
  clinic_name?: string;
  clinic_id?: number;
  current?: boolean;
  diabetic?: boolean;
  email?: string;
  first_name: string;
  fullname: string;
  id: number;
  last_name: string;
  last_timezone?: string;
  phone?: string;
  reviewed?: boolean;
  uuid: string;
  chat?: {
    message: string | null;
  };
  initial_weight?: number;
  feet: number;
  inches: number;
  gender: string;
  birthdate: string;
  timezone?: string;
}

interface Role {
  description: string;
  display_value: string;
  id: number;
  name: string;
}

export interface Provider {
  avatar: string;
  first_name: string;
  id: number;
  last_name: string;
  role: Role;
  role_names: string[];
  roles: Role[];
}

export type CurrentProgram = {
  id: number | null;
  displayValue: string;
  startDate: string;
  endDate: string | null;
  value: string;
  userProgramId: number | null;
  duration: number;
};

export type State = {
  slide: boolean | number;
  currentProgram: CurrentProgram;
  data: Member[];
  current: (Member & { preferences?: MemberPreference[] }) | null;
  /** Review lists: only members of this clinic are shown (null = all clinics) */
  clinicFilter: number | null;
};

type Payload = {
  [ActionTypes.CLEAR]: undefined;
  [ActionTypes.CLEAR_MESSAGE]: {
    memberId: number;
  };
  [ActionTypes.SELECT]: {
    id: number;
  };
  [ActionTypes.SET]: {
    patients: Member[];
    reviewedPatients: Member[];
  };
  [ActionTypes.SET_ASSIGNED_PROVIDERS]: {
    patientId: number;
    providers: Provider[];
  };
  [ActionTypes.SET_MESSAGE]: {
    memberId: number;
    message: string;
  };
  [ActionTypes.SET_PREFERENCES]: {
    preferences: MemberPreference[];
  };
  [ActionTypes.REVIEW]: {
    id: number;
  };
  [ActionTypes.REVIEW_MANY]: {
    ids: number[];
  };
  [ActionTypes.SET_PROGRAM]: {
    id: number | null;
    displayValue: string;
    startDate: string;
    endDate: string | null;
    value: string;
    userProgramId: number | null;
    duration: number;
  };
  [ActionTypes.SET_CLINIC_FILTER]: {
    clinicId: number | null;
  };
};

export type Action = DefaultAction<Payload>;

export const initialState: State = {
  slide: false,
  currentProgram: {
    id: null,
    displayValue: '',
    startDate: '',
    endDate: '',
    value: '',
    userProgramId: null,
    duration: 0,
  },
  data: [],
  current: null,
  clinicFilter: null,
};

/** Members visible under the current clinic filter (all of them when there is none) */
export const selectVisibleMembers = (state: State): Member[] =>
  state.clinicFilter === null
    ? state.data
    : state.data.filter((member) => member.clinic_id === state.clinicFilter);

export const reducer: Reducer<State, Action> = (state: State = initialState, action: Action) => {
  switch (action.type) {
    case ActionTypes.CLEAR: {
      return {
        slide: false,
        currentProgram: {
          id: null,
          displayValue: '',
          startDate: '',
          endDate: '',
          value: '',
          userProgramId: null,
          duration: 0,
        },
        data: [],
        current: state.current,
        clinicFilter: null,
      };
    }

    case ActionTypes.CLEAR_MESSAGE: {
      const { memberId } = action.payload;
      const updatedMembers = state.data.map((member) => {
        if (member.id === memberId) {
          return { ...member, chat: { message: null } };
        }
        return { ...member };
      });
      return {
        ...state,
        data: updatedMembers,
        current: updatedMembers.find(({ id }) => id === memberId) ?? null,
      };
    }

    case ActionTypes.SELECT: {
      const { id } = action.payload;
      const updatedMembers = state.data.map((member) => ({
        ...member,
        current: member.id === id,
      }));
      return {
        ...state,
        data: updatedMembers,
        current: updatedMembers.find((member) => member.id === id) ?? null,
      };
    }

    case ActionTypes.SET: {
      const { patients } = action.payload;
      const nonReviewedPatientsLength = patients.length;
      let { reviewedPatients } = action.payload;
      reviewedPatients = reviewedPatients || [];

      const updatedMembers = [
        ...patients.map((patient, i) => ({
          ...patient,
          reviewed: false,
          current: i === 0,
        })),
        ...reviewedPatients.map((patient, i) => ({
          ...patient,
          reviewed: true,
          current: nonReviewedPatientsLength === 0 && i === 0,
        })),
      ];
      return {
        ...state,
        data: updatedMembers,
        current: updatedMembers[0],
      };
    }

    case ActionTypes.SET_ASSIGNED_PROVIDERS: {
      const { patientId, providers } = action.payload;
      const updatedMembers = state.data.map((member) => {
        if (member.id === patientId) {
          return { ...member, assignedProviders: providers };
        }
        return member;
      });
      return {
        ...state,
        data: updatedMembers,
        current: updatedMembers.find(({ id }) => id === patientId) ?? null,
      };
    }

    case ActionTypes.SET_MESSAGE: {
      const { memberId, message } = action.payload;
      const updatedMembers = state.data.map((member) => {
        if (member.id === memberId) {
          return { ...member, chat: { message } };
        }
        return { ...member };
      });
      return {
        ...state,
        data: updatedMembers,
        current: updatedMembers.find(({ id }) => id === memberId) ?? null,
      };
    }

    case ActionTypes.SET_PREFERENCES: {
      const { preferences } = action.payload;

      const currentMemberId = state.current?.id ?? -1;
      const updatedMembers = state.data.map((member) => {
        if (member.id === currentMemberId) {
          return { ...member, preferences };
        }
        return { ...member };
      });

      return {
        ...state,
        data: updatedMembers,
        current: updatedMembers.find(({ id }) => id === currentMemberId) ?? null,
      };
    }

    case ActionTypes.REVIEW: {
      const { id } = action.payload;
      const updatedMembers = state.data.map((patient) => {
        if (patient.id === id) {
          return {
            ...patient,
            reviewed: !patient.reviewed,
          };
        }

        return patient;
      });
      return {
        ...state,
        slide: id,
        data: updatedMembers,
        current: updatedMembers.find((member) => member.id === id) ?? null,
      };
    }

    case ActionTypes.REVIEW_MANY: {
      const { ids } = action.payload;

      const hashWithMembers: { [key: number]: Member } = {};

      const members = state.data.map((member) => {
        const memberCopy = { ...member };
        hashWithMembers[Number(member.id)] = memberCopy;
        return memberCopy;
      });

      for (const id of ids) {
        if (id in hashWithMembers) {
          hashWithMembers[id].reviewed = true;
        }
      }

      return {
        ...state,
        data: members,
        current: (state.current
          ? members.find((member) => member.id === state.current?.id)
          : null) as State['current'],
      };
    }

    case ActionTypes.SET_CLINIC_FILTER: {
      const { clinicId } = action.payload;
      // A clinic no longer in the list (e.g. a stale link) falls back to all clinics
      const clinicFilter =
        clinicId !== null && state.data.some((member) => member.clinic_id === clinicId)
          ? clinicId
          : null;
      const visibleMembers = selectVisibleMembers({ ...state, clinicFilter });

      // Keep the current member when it is still visible, otherwise jump to the first one
      const currentId = visibleMembers.some((member) => member.id === state.current?.id)
        ? state.current?.id
        : visibleMembers[0]?.id;
      const updatedMembers = state.data.map((member) => ({
        ...member,
        current: member.id === currentId,
      }));

      return {
        ...state,
        clinicFilter,
        data: updatedMembers,
        current: updatedMembers.find((member) => member.id === currentId) ?? null,
      };
    }

    case ActionTypes.SET_PROGRAM:
      return {
        ...state,
        currentProgram: {
          id: action.payload.id,
          displayValue: action.payload.displayValue,
          startDate: action.payload.startDate,
          endDate: action.payload.endDate,
          value: action.payload.value,
          userProgramId: action.payload.userProgramId,
          duration: action.payload.duration,
        },
      };

    default:
      throw new Error('Unexpected action');
  }
};
